import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type ApiProject } from "../lib/api";

type Mode = "single" | "discover";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (project: ApiProject, info?: { discoveryStatus?: "ok" | "partial" | "failed" }) => void;
};

export function NewProjectModal({ open, onClose, onCreated }: Props) {
  const [mode, setMode] = useState<Mode>("single");
  const [name, setName] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode("single");
      setName("");
      setTargetUrl("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.createProject({
        name: name.trim(),
        targetUrl: targetUrl.trim(),
        discover: mode === "discover",
      });
      onCreated(res.project, { discoveryStatus: res.discoveryStatus });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError("Check that the URL starts with http:// or https:// and try again.");
      } else {
        setError("Couldn’t create the project. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="text-lg font-semibold">New project</h2>
        <p className="mt-1 text-sm text-neutral-600">
          {mode === "single"
            ? "Paste the URL of the page you want to collect feedback on."
            : "Paste your site’s entry URL — we’ll add a page for every linked HTML page we find."}
        </p>

        {/* Mode toggle */}
        <div className="mt-4 inline-flex rounded-md bg-neutral-100 p-0.5 text-xs">
          <ModeButton
            label="Single URL"
            active={mode === "single"}
            disabled={busy}
            onClick={() => setMode("single")}
          />
          <ModeButton
            label="Discover all pages"
            active={mode === "discover"}
            disabled={busy}
            onClick={() => setMode("discover")}
          />
        </div>

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Project name
            </span>
            <input
              type="text"
              required
              maxLength={120}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Marketing homepage v3"
              disabled={busy}
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none disabled:bg-neutral-50"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {mode === "single" ? "Target URL" : "Entry URL"}
            </span>
            <input
              type="url"
              required
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              placeholder={mode === "discover" ? "https://lhi-design.pages.dev/" : "https://avista.is/"}
              disabled={busy}
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none disabled:bg-neutral-50"
            />
            {mode === "discover" ? (
              <span className="mt-1 block text-xs text-neutral-500">
                We follow internal links up to 2 levels deep, capped at 30 pages.
                You can rename and remove pages after.
              </span>
            ) : null}
          </label>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {busy && mode === "discover" ? (
            <p className="text-sm text-neutral-600">
              <DiscoverSpinner /> Discovering pages — this may take 10–30&nbsp;seconds.
            </p>
          ) : null}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
            >
              {busy
                ? mode === "discover"
                  ? "Discovering…"
                  : "Creating…"
                : mode === "discover"
                  ? "Discover and create"
                  : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModeButton({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={
        "rounded px-2.5 py-1 transition " +
        (active
          ? "bg-white text-neutral-900 shadow-sm"
          : "text-neutral-500 hover:text-neutral-900") +
        (disabled ? " opacity-50" : "")
      }
    >
      {label}
    </button>
  );
}

function DiscoverSpinner() {
  return (
    <span
      aria-hidden
      className="mr-1 inline-block h-3 w-3 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900 align-[-2px]"
    />
  );
}
